import * as React from 'react';
import { useState } from 'react';
import { PatientTrackables, TrackableMeasure, TrackableMeasureTypes } from '../../../../models/core';
import { Typography, Grid, Box, IconButton } from '@mui/material';

import { GridFormItem } from '../../../GridForm';
import useSnackbar from '../../../../hooks/useSnackbar';
import AlertSnack from '../../../ErrorSnack'

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TextTable from '../../../TextTable';
import { MeasureEntryList, ComplianceList } from '../../../../utils/enums';
import { MeasureEditorViewSection } from './MeasureEditorViewSection';
import { MeasureEditorViewState } from './MeasureEditorViewState';
import { patientTrackablesToState, copyTableConditionContent } from './MeasureTools'

interface MeasureEditorViewProps {
    title?:string;
    sections?:MeasureEditorViewSection[];
    patientTrackables?:PatientTrackables;
    initialState?:MeasureEditorViewState;
    readonly?:boolean;
    onStateUpdate?:(state:MeasureEditorViewState) => void;
}

const checkRenderSection = (section:MeasureEditorViewSection, array:MeasureEditorViewSection[]|undefined, def = true) => {
    return (!array || array?.length == 0) ? def : array?.findIndex((s) => section == s) >= 0;
}

const MeasureEditorView = ({ title, sections, patientTrackables, readonly=false, initialState, onStateUpdate }: MeasureEditorViewProps): JSX.Element => {

    const activeState = React.useMemo(() => {
        return patientTrackablesToState(patientTrackables, initialState)
    }, [patientTrackables, initialState]);
    
    const { showError: snackError, ...snackProps } = useSnackbar();
    const [measureList, setMeasureList] = useState<TrackableMeasure[]>(activeState.measures || []);
    const [suspectConditionList, setSuspectConditionList] = useState<TrackableMeasure[]>(activeState.suspect || []);
    const [chronicConditionList, setChronicConditionList] = useState<TrackableMeasure[]>(activeState.chronic || []);
    const [diagnosisList, setDiagnosisList] = useState<TrackableMeasure[]>(activeState.diagnosis || []);

    const [measuresUpdated, setMeasuresUpdated] = useState(activeState.measuresUpdated||false);
    const [suspectUpdated, setSuspectUpdated] = useState(activeState.suspectUpdated||false);
    const [chronicUpdated, setChronicUpdated] = useState(activeState.chronicUpdated||false);
    const [diagnosedUpdated, setDiagnosedUpdated] = useState(activeState.diagnosisUpdated||false);
    
    React.useEffect(() => {
        onStateUpdate && onStateUpdate({
            measuresUpdated:measuresUpdated,
            suspectUpdated:suspectUpdated,
            chronicUpdated:chronicUpdated,
            diagnosisUpdated:diagnosedUpdated,
            measures: measureList,
            suspect: suspectConditionList,
            chronic: chronicConditionList,
            diagnosis: diagnosisList,
        });
    },[onStateUpdate, measureList, suspectConditionList, chronicConditionList, diagnosisList,
        measuresUpdated, suspectUpdated, chronicUpdated, diagnosedUpdated]);

    React.useEffect(() => {
        setMeasureList(activeState.measures || []);
        setSuspectConditionList(activeState.suspect || []);
        setChronicConditionList(activeState.chronic || []);
        setDiagnosisList(activeState.diagnosis || []);
        setMeasuresUpdated(activeState.measuresUpdated||false);
        setSuspectUpdated(activeState.suspectUpdated||false);
        setChronicUpdated(activeState.chronicUpdated||false);
        setDiagnosedUpdated(activeState.diagnosisUpdated||false);
    },[activeState]);

    //  Demographics
    const demographicsComponents = React.useMemo<JSX.Element>(() => {
        return checkRenderSection(MeasureEditorViewSection.Demographics, sections, true) ? (<GridFormItem xs={12}>
            <Grid container spacing={2}>
                <GridFormItem xs={12}>
                    <Typography variant='h5' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>{title || 'Conditions'}</Typography>
                </GridFormItem>   
            </Grid>
            <GridFormItem xs={12}><Box sx={{ height: '0.5em' }} /></GridFormItem>
            </GridFormItem>) : <></>;
    },[sections, title]);

    const measureListKeys = React.useMemo(() => {
        const sortedList = MeasureEntryList.sort((a, b) => a.name.localeCompare(b.name));
        return sortedList?.map((it) => { return { key:it.name, value:it.code } });
    },[]);

    const complianceListKeys = React.useMemo(() => {
        return ComplianceList?.map((it) => { return { key:it.name, value:it.code } });
    },[]);

    const copyTableMeasureContent = React.useCallback((contentName:string, content: TrackableMeasure[]) => {
        let copyData:string = contentName;
        content?.forEach((it) => {
            const measureData = measureListKeys?.find((fi) => {return fi.value === it.name;});
            if(measureData) {
                copyData = copyData + `\n${measureData.key}`;
            }
        });
        navigator.clipboard.writeText(copyData);
    },[measureListKeys])

    const checkCanAddRow = React.useCallback((nr: TrackableMeasure, cbk:()=>void) => {
        if(!nr.name || nr.name == '') {
            snackError(new Error('Measure must be set'));
            return [{ key:'measure', label:'Measure must be set' }];
        }
        cbk();
        return undefined;
    }, [snackError]);

    const createMeasureComps = React.useCallback((
        setList:(value:React.SetStateAction<TrackableMeasure[]>) => void,
        setChange:(value:React.SetStateAction<boolean>) => void,
        list:TrackableMeasure[],
    ) => {
        const handleAddRow = (newRow: TrackableMeasure) => {
            setList([...list, {...newRow, id:Date.now().toString(), 
                    type:TrackableMeasureTypes.MEASURE, created:Date.now().toString()}]);
            setChange(true);
        };

        const handleDeleteRow = (newRow: TrackableMeasure) => {
            setList([...list.filter(el => el.id != newRow.id)]);
            setChange(true);
        };

        const handleEditRow = (newRow: TrackableMeasure) => {
            const index = list.findIndex(el => el.id == newRow.id && (el.name !== newRow.name || newRow.value !== el.value));
            if(index !== -1) {
                setList(prevArray => {
                    const newArray = [...prevArray];
                    newArray[index] = newRow
                    return newArray;
                });
                setChange(true);
            }
        };

        return checkRenderSection(MeasureEditorViewSection.Measures, sections, true) ? (
            <Grid container spacing={2}>
            <GridFormItem xs={11}>
                <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>Measures</Typography>
            </GridFormItem>
            <GridFormItem xs={1}>
                <IconButton onClick={() => copyTableMeasureContent('Measures', list)}><ContentCopyIcon/></IconButton>
            </GridFormItem>
            <GridFormItem xs={12}>
                <TextTable
                    readonly={readonly}
                    addRowHeader={"Select Measure:"}
                    rows={list}
                    columns={[
                        { key: 'name', label: 'Measure', entries:measureListKeys, type:'enum' },
                        { key: 'value', label: 'State', entries:complianceListKeys, type:'enum', default: 'NC' },
                    ]}
                    onAddRow={handleAddRow}
                    canAddRow={checkCanAddRow}
                    onDeleteRow={handleDeleteRow}
                    onUpdateRow={handleEditRow}
                />
            </GridFormItem>
            <GridFormItem xs={12}><Box sx={{ height: '0.5em' }} /></GridFormItem>
            </Grid>
            ) : <></>;
    },[copyTableMeasureContent, checkCanAddRow, sections, measureListKeys, complianceListKeys, readonly]);


    const measureComponents = React.useMemo<JSX.Element>(() => {
        return createMeasureComps(setMeasureList, setMeasuresUpdated, measureList);
    },[measureList, createMeasureComps]);


    const createEditConditionComp = React.useCallback((
        readonly:boolean,
        setList:(value:React.SetStateAction<TrackableMeasure[]>) => void,
        setChange:(value:React.SetStateAction<boolean>) => void,
        verifyRow:(nr: TrackableMeasure, cbk: () => void) => {key: string; label: string;}[] | undefined,
        list:TrackableMeasure[],
        title:string,
        addRowHeader:string,
        section:MeasureEditorViewSection,
        sections:MeasureEditorViewSection[] | undefined,
        trackableType:TrackableMeasureTypes
    ) => {
        const handleAddRow = (newRow: TrackableMeasure) => {
            setList([...list, {...newRow, id:Date.now().toString(), type:trackableType, created:Date.now().toString() }]);
            setChange(true);
        };

        const handleDeleteRow = (newRow: TrackableMeasure) => {
            setList([...list.filter(el => el.id != newRow.id)]);
            setChange(true);
        };

        const handleEditRow = (newRow: TrackableMeasure) => {
            const index = list.findIndex(el => el.id == newRow.id);
            if(index !== -1) {
                setList(prevArray => {
                    const newArray = [...prevArray];
                    newArray[index] = newRow;
                    return newArray;
                });
                setChange(true);
            }
        };

        return checkRenderSection(section, sections, true) ? (
            <Grid container spacing={2}>
            <GridFormItem xs={11}>
                <Typography variant='h6' component='div' sx={{ display: 'flex', justifyContent: 'start' }}>{title}</Typography>
            </GridFormItem>
            <GridFormItem xs={1}>
                <IconButton onClick={() => copyTableConditionContent(title, list)}><ContentCopyIcon/></IconButton>
            </GridFormItem>
            <GridFormItem xs={12}>
                <TextTable
                    readonly={readonly}
                    addRowHeader={addRowHeader}
                    rows={list}
                    columns={[
                        { key: 'name', label: 'Name' },
                        { key: 'value', label: 'ICD' }
                    ]}
                    onAddRow={handleAddRow}
                    canAddRow={verifyRow}
                    onDeleteRow={handleDeleteRow}
                    onUpdateRow={handleEditRow}
                />
            </GridFormItem>
            <GridFormItem xs={12}><Box sx={{ height: '0.5em' }} /></GridFormItem>
            </Grid>
            ) : <></>;
    },[]);

    const suspectConditionComponents = React.useMemo<JSX.Element>(() => {
        return createEditConditionComp(readonly, setSuspectConditionList, setSuspectUpdated, checkCanAddRow,
            suspectConditionList, 'Suspect Conditions', 'Enter the suspect condition and ICD code:',
            MeasureEditorViewSection.Suspect, sections, TrackableMeasureTypes.SUSPECT
        );
    },[suspectConditionList, createEditConditionComp, checkCanAddRow, sections, readonly]);

    const chronicConditionComponents = React.useMemo<JSX.Element>(() => {
        return createEditConditionComp(readonly, setChronicConditionList, setChronicUpdated, checkCanAddRow,
            chronicConditionList, 'Chronic Conditions', 'Enter the chronic condition and ICD code:',
            MeasureEditorViewSection.Chronic, sections, TrackableMeasureTypes.CHRONIC
        );
    },[chronicConditionList, createEditConditionComp, checkCanAddRow, sections, readonly]);

    const diagnosisComponents = React.useMemo<JSX.Element>(() => {
        return createEditConditionComp(readonly, setDiagnosisList, setDiagnosedUpdated, checkCanAddRow,
            diagnosisList, 'Diagnosis', "Enter the diagnosis and ICD code:",MeasureEditorViewSection.Diagnosis, 
            sections, TrackableMeasureTypes.DIAGNOSIS
        );
    },[diagnosisList, checkCanAddRow, createEditConditionComp, sections, readonly]);

    return <Grid container spacing={2}>
        <GridFormItem xs={12}>
            { demographicsComponents } { measureComponents } { suspectConditionComponents }{ chronicConditionComponents }{ diagnosisComponents }
        </GridFormItem>
        <AlertSnack {...snackProps}/>
    </Grid>
}

export default MeasureEditorView;